import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PATHWAYS,
  SUB_PATHWAYS_C,
  SCENARIOS,
} from '../data/checklist';
import { loadSelection, saveSelection } from '../lib/selection';
import type {
  Pathway,
  Scenario,
  Selection,
  SubPathwayC,
} from '../data/types';

export function SelectorPage() {
  const navigate = useNavigate();
  const [sel, setSel] = useState<Selection>(() => loadSelection());

  // Auto-persist as the user changes it — they may navigate away mid-pick
  // and we want their state to survive.
  useEffect(() => {
    saveSelection(sel);
  }, [sel]);

  function pickPathway(p: Pathway) {
    setSel((prev) => ({
      ...prev,
      pathway: p,
      // Reset sub-pathway when leaving C
      subPathwayC: p === 'C' ? prev.subPathwayC : null,
    }));
  }

  function pickSubPathway(c: SubPathwayC) {
    setSel((prev) => ({ ...prev, subPathwayC: c }));
  }

  function toggleScenario(s: Scenario) {
    setSel((prev) => ({
      ...prev,
      scenarios: prev.scenarios.includes(s)
        ? prev.scenarios.filter((x) => x !== s)
        : [...prev.scenarios, s],
    }));
  }

  const canContinue =
    !!sel.pathway &&
    (sel.pathway !== 'C' || !!sel.subPathwayC) &&
    sel.scenarios.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
      <div className="eyebrow mb-4">Step 1 of 1 · Pathway &amp; Scenarios</div>
      <h1 className="editorial-h1 mb-3">
        What is <em className="italic text-accent">your LEA's</em> participation shape?
      </h1>
      <p className="editorial-deck mb-12 max-w-2xl">
        Tell us how your LEA participates and how services are delivered. We'll
        filter the 280-item checklist to only what applies to you.
      </p>

      {/* ----------- Pathway ----------- */}
      <section className="mb-12">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
          Pathway
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-rule border border-rule">
          {PATHWAYS.map((p) => {
            const selected = sel.pathway === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => pickPathway(p.id)}
                className={
                  'bg-paper text-left p-6 transition-colors ' +
                  (selected ? 'ring-2 ring-accent ring-inset' : 'hover:bg-paper-deep')
                }
              >
                <div className="font-editorial italic text-xs text-accent mb-2">
                  Pathway {p.id}
                </div>
                <div className="font-editorial text-[20px] font-medium text-ink mb-2 leading-snug">
                  {p.name}
                </div>
                <div className="font-editorial text-[14px] text-ink-soft leading-relaxed">
                  {p.description}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ----------- Sub-pathway (only when C) ----------- */}
      {sel.pathway === 'C' && (
        <section className="mb-12">
          <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
            Pathway C — Sub-pathway
          </div>
          <div className="space-y-px bg-rule border border-rule">
            {SUB_PATHWAYS_C.map((sp) => {
              const selected = sel.subPathwayC === sp.id;
              return (
                <button
                  key={sp.id}
                  type="button"
                  onClick={() => pickSubPathway(sp.id)}
                  className={
                    'w-full bg-paper text-left p-5 transition-colors flex gap-5 items-start ' +
                    (selected ? 'ring-2 ring-accent ring-inset' : 'hover:bg-paper-deep')
                  }
                >
                  <div className="font-editorial italic text-sm text-accent shrink-0 w-10">
                    {sp.id}
                  </div>
                  <div>
                    <div className="font-editorial text-[17px] font-medium text-ink mb-1 leading-snug">
                      {sp.name}
                    </div>
                    <div className="font-editorial text-[14px] text-ink-soft leading-relaxed">
                      {sp.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ----------- Scenarios ----------- */}
      <section className="mb-12">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-2">
          Service delivery scenarios
        </div>
        <p className="font-editorial italic text-[15px] text-ink-muted mb-5">
          Select all that apply — many LEAs combine multiple delivery modes.
        </p>
        <div className="space-y-px bg-rule border border-rule">
          {SCENARIOS.map((s) => {
            const selected = sel.scenarios.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => toggleScenario(s.id)}
                className={
                  'w-full bg-paper text-left p-5 transition-colors flex gap-5 items-start ' +
                  (selected ? 'bg-paper-deep' : 'hover:bg-paper-deep')
                }
                aria-pressed={selected}
              >
                <div
                  className={
                    'shrink-0 w-5 h-5 mt-1 border ' +
                    (selected
                      ? 'bg-accent border-accent'
                      : 'border-ink-muted bg-transparent')
                  }
                  aria-hidden="true"
                >
                  {selected && (
                    <svg viewBox="0 0 24 24" className="w-full h-full fill-paper">
                      <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-editorial italic text-xs text-accent mb-1">
                    {s.id}
                  </div>
                  <div className="font-editorial text-[17px] font-medium text-ink mb-1 leading-snug">
                    {s.name}
                  </div>
                  <div className="font-editorial text-[14px] text-ink-soft leading-relaxed">
                    {s.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ----------- Submit ----------- */}
      <div className="border-t border-rule pt-7 flex items-center justify-between flex-wrap gap-4">
        <div className="font-editorial italic text-[15px] text-ink-muted max-w-md">
          You can change your selections anytime — the checklist will re-filter.
        </div>
        <button
          type="button"
          disabled={!canContinue}
          onClick={() => navigate('/preview')}
          className={
            'btn-text ' +
            (canContinue ? '' : 'opacity-40 cursor-not-allowed pointer-events-none')
          }
        >
          See my checklist
        </button>
      </div>
    </div>
  );
}
